<template>
  <div>
    <BioEditorHeader
      :bio="teacherBio"
      @recordAudio="recordAudio"
      @uploadAudio="uploadAudio"
      @removeAudio="removeAudio"
      @toggleLockAudio="toggleLockAudio"
    />
    <h2 class="my-4">{{ $t('SETTINGS.TITLE.LINKS') }}</h2>
    <draggable
      :delay="draggableDelay"
      v-if="sortedLinks.length"
      v-model="sortedLinks"
      @end="reorderLinks"
      :options="{ handle: '.drag-handle' }"
      class="mt-3"
    >
      <BioEditLink
        class="mb-4"
        v-for="link in sortedLinks"
        :key="link.id"
        :link="link"
        @editLink="editLink(link)"
        @deleteLink="deleteLink(link.id)"
        @saveLink="saveLink(link)"
      />
    </draggable>
    <div class="row">
      <div class="col d-flex justify-content-end mt-4">
        <HMButton color="outline-gray" @click="addLink">
          <i class="far fa-link c-brand-color mr-3"></i>
          {{ $t('SETTINGS.BUTTON.ADD_ANOTHER_LINK') }}
        </HMButton>
      </div>
    </div>
    <hr class="mt-4" />
    <h2 class="mt-5">
      {{ $t('SETTINGS.TITLE.MONETIZE') }}
    </h2>
    <div
      class="form-group d-flex my-3"
      :class="{
        'flex-column flex-md-row align-items-start align-items-md-center justify-content-start justify-content-md-between': !isStripeConnected,
        'flex-row align-items-center justify-content-between': isStripeConnected,
      }"
    >
      <h4 class="m-0 mr-3">
        {{ $t('BIO.LABEL.SHOW_TIP_JAR_LINK') }}
      </h4>
      <router-link
        v-if="!isStripeConnected"
        :to="{ name: 'bio-settings', hash: '#stripe' }"
        class="undecorated-router-link c-coral d-flex"
      >
        <HMButton
          class="mt-3 mt-md-0"
          color="outline-gray"
          v-tooltip="{
            content: isStripeConnected
              ? ''
              : $t('SETTINGS.TEXT.ENABLE_STRIPE_FOR_TIP_JAR_IN_BIO'),
            delay: 100,
            placement: 'top',
            container: 'body',
            trigger: 'click hover',
          }"
        >
          <i class="far fa-plus c-brand-color mr-3"></i>
          {{ $t('BIO.BUTTON.CONNECT_STRIPE') }}
        </HMButton>
      </router-link>
      <HMSwitch
        v-else
        v-model="teacherBio.tip_jar_enabled"
        @change.native="setBioInfo"
      >
      </HMSwitch>
    </div>
    <small>
      {{ $t('SETTINGS.TEXT.ENABLE_TIP_JAR_BIO') }}
    </small>
    <div
      class="form-group d-flex mt-4 mb-3"
      :class="{
        'flex-column flex-md-row align-items-start align-items-md-center justify-content-start justify-content-md-between': isBioAccount,
        'flex-row align-items-center justify-content-between': !isBioAccount,
      }"
    >
      <HMLock
        :locked="isBioAccount"
        iconClass="far fa-crown"
        :iconTooltip="$t('BIO.TEXT.PREMIUM_FEATURE_FOR_MARVELOUS_CUSTOMERS')"
      >
        <span class="h4 m-0 mr-3">
          {{ $t('BIO.LABEL.SHOW_CALENDAR_LINK') }}
        </span>
      </HMLock>
      <router-link
        v-if="isBioAccount"
        :to="{ name: 'bio-upgrade' }"
        class="undecorated-router-link c-coral d-flex"
      >
        <HMButton
          class="mt-3 mt-md-0"
          color="outline-gray"
          v-tooltip="{
            content: !isBioAccount
              ? ''
              : $t('SETTINGS.TEXT.UPGRADE_TO_ENABLE_CALENDAR'),
            delay: 100,
            placement: 'top',
            container: 'body',
            trigger: 'click hover',
          }"
        >
          <i class="far fa-plus c-brand-color mr-3"></i>
          {{ $t('BIO.BUTTON.UPGRADE_TO_ENABLE') }}
        </HMButton>
      </router-link>
      <HMSwitch
        v-else
        v-model="teacherBio.calendar_enabled"
        @change.native="setBioInfo"
      >
      </HMSwitch>
    </div>
    <small v-if="isBioAccount">
      {{ $t('SETTINGS.TEXT.ENABLE_CALENDAR_BIO_LOCKED') }}
    </small>
    <small v-else>
      {{ $t('SETTINGS.TEXT.ENABLE_CALENDAR_BIO') }}
    </small>
    <hr class="my-4" />
    <div class="row" v-if="teacherModel">
      <div class="col-lg-12">
        <h2 class="mb-4">
          {{ $t('SETTINGS.LABEL.SOCIAL_LINKS') }}
        </h2>
      </div>
      <div class="col-lg-6 px-3">
        <HMTextInput
          type="text"
          :label="$t('SETTINGS.LABEL.FACEBOOK_GROUP')"
          :placeholder="placeholderText"
          v-model="teacherModel.facebook_group"
          name="Facebook Group link"
        />
      </div>
      <div class="col-lg-6 px-3">
        <HMTextInput
          type="text"
          :label="$t('SETTINGS.LABEL.FACEBOOK_PAGE')"
          :placeholder="placeholderText"
          v-model="teacherModel.facebook_page"
          name="Facebook Page link"
        />
      </div>
      <div class="col-lg-6 pt-3 px-3">
        <HMTextInput
          type="text"
          :label="$t('SETTINGS.LABEL.INSTAGRAM')"
          :placeholder="placeholderText"
          v-model="teacherModel.instagram"
          name="Instagram link"
        />
      </div>
      <div class="col-lg-6 pt-3 px-3">
        <HMTextInput
          type="text"
          :label="$t('SETTINGS.LABEL.TWITTER')"
          :placeholder="placeholderText"
          v-model="teacherModel.twitter"
          name="Twitter link"
        />
      </div>
      <div class="col-lg-6 pt-3 px-3">
        <HMTextInput
          type="text"
          :label="$t('SETTINGS.LABEL.PINTEREST')"
          :placeholder="placeholderText"
          v-model="teacherModel.pinterest"
          name="Pinterest link"
        />
      </div>
      <div class="col-lg-6 pt-3 px-3">
        <HMTextInput
          type="text"
          :label="$t('SETTINGS.LABEL.YOUTUBE')"
          :placeholder="placeholderText"
          v-model="teacherModel.youtube_channel"
          name="Youtube link"
        />
      </div>
      <div class="col-lg-6 pt-3 px-3">
        <HMTextInput
          type="text"
          :label="$t('SETTINGS.LABEL.LINKEDIN')"
          :placeholder="placeholderText"
          v-model="teacherModel.linkedin_page"
          name="LinkedIn Link"
        />
      </div>
      <div class="col-lg-6 pt-3 px-3">
        <HMTextInput
          type="text"
          class="mb-0"
          :label="$t('SETTINGS.LABEL.WEBSITE')"
          :placeholder="placeholderText"
          v-model="teacherModel.web_link"
          name="LinkedIn Link"
        />
      </div>
      <div class="col-lg-6 pt-3 px-3">
        <HMTextInput
          type="text"
          class="mb-0"
          :label="$t('SETTINGS.LABEL.TIK_TOK')"
          :placeholder="placeholderText"
          v-model="teacherModel.tiktok_link"
          name="LinkedIn Link"
        />
      </div>
      <div class="col-12 d-flex justify-content-end">
        <HMButton
          class="mt-3 mt-lg-0"
          color="brand"
          @click="saveSocialLinks"
          :disabled="socialLinksInProgress"
          :loading="socialLinksInProgress"
        >
          {{ $t('GENERAL.BUTTON.SAVE') }}
        </HMButton>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { errorToMessage } from '@/core/utils/errors';
import { draggableDelay } from '@/web/utils/draggable';
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import LinkPhotoUploadModal from '@/web/components/settings/BioLinkUpdateModal.vue';
import BioEditLink from '@/web/components/bio/BioEditLink.vue';
import { cloneDeep, pick } from 'lodash';
import { formatObjectForBackend } from '@/core/api/utils/converter';
import BioEditorHeader from '@/web/components/bio/BioEditorHeader.vue';
import AudioRecordingModal from '@/web/components/community/AudioRecordingModal.vue';
import { upload, deleteFile } from '@/web/services/upload';
import { HMAudioUploadModal } from '@/web/components/common';
import HMDialog from '@/web/components/common/HMDialog';

export default Vue.extend({
  components: {
    draggable,
    BioEditorHeader,
    BioEditLink,
  },
  name: 'BioTemplates',
  data() {
    return {
      draggableDelay,
      image: null,
      teacherBio: {},
      sortedLinks: [],
      placeholderText: 'https://',
      teacherModel: null,
      socialLinksInProgress: false,
      isUploading: false,
      uploadProgress: 0,
    };
  },
  computed: {
    ...mapGetters({
      bioLinks: 'setting/bioLinks',
      teacher: 'user/teacher',
    }),
    isStripeConnected() {
      return this.teacher?.is_stripe_connected;
    },
    isBioAccount() {
      return this.teacher?.is_bio;
    },
  },
  methods: {
    async setBioInfo() {
      try {
        await this.$store.dispatch(
          'setting/updateMarvelousBioInfo',
          formatObjectForBackend(cloneDeep(this.teacherBio))
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$tc('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$tc('SETTINGS.NOTIFICATIONS.MARVELOUS_BIO_INFO_SAVED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      }
    },
    async addLink() {
      //this.$modal.show(LinkPhotoUploadModal);
      try {
        await this.$store.dispatch('setting/addBioLink', {
          position: this.sortedLinks.length,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Could not add a new link',
          text: errorToMessage(error.response),
        });
      }
    },
    deleteLink(id) {
      try {
        this.$store.dispatch('setting/deleteBioLink', id);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      }
    },
    async reorderLinks() {
      const links = this.sortedLinks.map((link, index) => {
        return {
          ...link,
          image: link.image?.id,
          position: index,
        };
      });
      await this.$store.dispatch('setting/reorderBioLinks', links);
      //await this.$store.dispatch('setting/getMarvelousBioInfo');
    },
    editLink(link) {
      this.$modal.show(LinkPhotoUploadModal, { link });
    },
    async saveLink(link) {
      try {
        await this.$store.dispatch('setting/updateBioLink', {
          ...link,
          image: link.image?.id,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      }
    },
    sortPosition(items) {
      this.sortedLinks = items.sort((a, b) => a.position - b.position);
    },
    resetTeacherModel() {
      this.teacherModel = pick(this.teacher, [
        'facebook_group',
        'facebook_page',
        'instagram',
        'twitter',
        'pinterest',
        'youtube_channel',
        'linkedin_page',
        'web_link',
        'tiktok_link',
      ]);
    },
    async saveSocialLinks() {
      this.socialLinksInProgress = true;
      const teacherModel = cloneDeep(this.teacherModel);

      await this.$store.dispatch('user/updateTeacher', {
        teacher: teacherModel,
      });
      this.socialLinksInProgress = false;
    },
    uploadAudio() {
      this.$modal.show(HMAudioUploadModal, {
        title: this.$t('BIO.LABEL.UPLOAD_MP3_AUDIO'),
        description: '',
        confirm: this.audioFileUploaded,
        isCanvaEnabled: false,
        isUnsplashEnabled: false,
        accept: 'audio/*',
      });
    },
    async removeAudio() {
      if (!this.teacherBio.audio) return;

      const title = this.$t('BIO.TITLE.DELETE_AUDIO');
      const subtitle = null;
      const text = this.$t('BIO.TEXT.DELETE_AUDIO_DESCRIPTION');
      const confirmButton = this.$t('GENERAL.BUTTON.DELETE');

      const confirm = async () => {
        try {
          await deleteFile(this.teacherBio.audio.id);
          this.teacherBio.audio = null;
          this.setBioInfo();
        } catch (error) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Audio removal failed',
            text: errorToMessage(error?.response, `Request failed: ${error}`),
            duration: -1, // stay ON
          });
        }
      };

      this.$modal.show(HMDialog, {
        title,
        subtitle,
        text,
        confirmButton,
        confirm,
      });
    },
    async toggleLockAudio() {
      if (!this.teacherBio.audio) return;

      try {
        await this.$store.dispatch('mediaLibrary/toggleFileDeletable', {
          id: this.teacherBio.audio.id,
        });

        this.teacherBio.audio.deletable = !this.teacherBio.audio.deletable;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Audio locking failed',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    recordAudio() {
      this.$modal.show(AudioRecordingModal, {
        onRecordingAvailable: this.onRecordAudioConfirmed,
      });
    },
    async audioFileUploaded(file) {
      try {
        this.isUploading = true;
        console.log('uploaded audio: ', file);
        this.teacherBio.audio = file;
        await this.setBioInfo();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Audio upload failed',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isUploading = false;
      }
    },
    async onRecordAudioConfirmed(inputFile) {
      try {
        this.isUploading = true;
        this.uploadProgress = 0;
        const file = await upload(inputFile);
        console.log('uploaded audio: ', file);
        this.teacherBio.audio = file;
        await this.setBioInfo();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Audio upload failed',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isUploading = false;
      }
    },
  },
  async mounted() {
    this.teacherBio = await this.$store.dispatch('setting/getMarvelousBioInfo');
    if (!this.bioLinks?.length) {
      this.addLink();
    }
    this.resetTeacherModel();
  },
  watch: {
    bioLinks: {
      handler(value) {
        this.sortPosition([...cloneDeep(this.bioLinks)]);
      },
      deep: true,
    },
    teacher() {
      this.resetTeacherModel();
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

::v-deep .hm-file-upload-inner-container {
  width: 100%;
}
.justify-content-stretch {
  justify-content: stretch;
}
.link {
  color: $dark-gray;
  text-decoration: none;
}

.color-picker-component {
  flex: 1;
  width: 100%;
  margin-top: 0.5rem;
}
.default-image-container {
  margin-left: auto;
  margin-right: auto;
}
@include media-breakpoint-up(md) {
  .color-picker-component {
    margin-top: 0;
    width: auto;
  }
  .default-image-container {
    margin-left: 0;
    margin-right: 0;
  }
}
.btn-outline-white:hover {
  color: white;
}
.flex-container {
  flex: 1;
}
</style>
